import React, { useState, useEffect } from 'react';
import { db } from '../../firebase'; 
import { collection, getDocs, serverTimestamp, query, orderBy, doc, updateDoc, getDoc, setDoc, deleteDoc } from 'firebase/firestore'; // Added deleteDoc
import { useNavigate } from 'react-router-dom';

// --- Cloudinary Configuration ---
const CLOUD_NAME = "invoice-app";       
const API_KEY = "149469791857382";      
const API_SECRET = "CQJwV5ujAkgYO5BHYKMkGIITbMU"; 

const Setting = () => {
    const navigate = useNavigate(); 
    
    // --- Tabs State ---
    const [activeTab, setActiveTab] = useState('clients'); // 'clients' or 'agents'

    // --- Client States ---
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    const [newCompanyName, setNewCompanyName] = useState('');
    const [newCustomerName, setNewCustomerName] = useState('');
    const [newAddress, setNewAddress] = useState('');
    const [newPhone, setNewPhone] = useState(''); 
    const [newAddFile, setNewAddFile] = useState(null); 
    const [isUploading, setIsUploading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);       
    const [viewModalOpen, setViewModalOpen] = useState(false); 
    const [editingCompany, setEditingCompany] = useState(null); 
    const [viewingCompany, setViewingCompany] = useState(null); 
    
    const [editCompanyName, setEditCompanyName] = useState('');
    const [editCustomerName, setEditCustomerName] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [newEditFile, setNewEditFile] = useState(null); 
    const [isUpdating, setIsUpdating] = useState(false); 

    const [previewFile, setPreviewFile] = useState(null); 

    // --- Booking Agent States ---
    const [bookingAgents, setBookingAgents] = useState([]);
    const [newAgentName, setNewAgentName] = useState('');
    const [newAgentContact, setNewAgentContact] = useState('');
    const [newAgentPhone, setNewAgentPhone] = useState('');
    const [newAgentAddress, setNewAgentAddress] = useState('');
    
    const [agentModalOpen, setAgentModalOpen] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [editAgentName, setEditAgentName] = useState('');
    const [editAgentContact, setEditAgentContact] = useState('');
    const [editAgentPhone, setEditAgentPhone] = useState('');
    const [editAgentAddress, setEditAgentAddress] = useState('');
    
    useEffect(() => { 
        fetchCompanies(); 
        fetchBookingAgents(); // Fetch agents on load
    }, []);

    // --- Fetch Data ---
    const fetchCompanies = async () => {
        setLoading(true); 
        try {
            const q = query(collection(db, 'companies'), orderBy('name'));
            const querySnapshot = await getDocs(q);
            const companyList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), files: doc.data().files || [] }));
            setCompanies(companyList);
        } catch (error) { console.error("Error fetching companies: ", error); }
        setLoading(false); 
    };

    const fetchBookingAgents = async () => {
        try {
            const q = query(collection(db, 'booking_companies'), orderBy('name'));
            const snap = await getDocs(q);
            setBookingAgents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) { console.error("Error fetching agents: ", error); }
    };
    
    // --- Cloudinary Helpers ---
    const generateSignature = async (params, secret) => {
        const sortedKeys = Object.keys(params).sort();
        const stringToSign = sortedKeys.map(key => `${key}=${params[key]}`).join('&') + secret;
        const encoder = new TextEncoder();
        const data = encoder.encode(stringToSign);
        const hashBuffer = await crypto.subtle.digest('SHA-1', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    };

    const getDownloadUrl = (url) => url ? url.replace('/upload/', '/upload/fl_attachment/') : '';

    const uploadToCloudinary = async (file, companyId) => {
        const timestamp = Math.round((new Date()).getTime() / 1000);
        const folderName = `company_docs/${companyId}`; 

        const params = { folder: folderName, timestamp: timestamp };
        const signature = await generateSignature(params, API_SECRET);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", API_KEY);
        formData.append("timestamp", timestamp);
        formData.append("folder", folderName);
        formData.append("signature", signature);

        const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`, {
            method: "POST", body: formData
        });
        const data = await response.json();
        if (data.error) throw new Error(data.error.message);
        
        return {
            id: Date.now().toString(), fileName: file.name, fileUrl: data.secure_url, 
            public_id: data.public_id, resource_type: data.resource_type, format: data.format
        };
    };

    const deleteFromCloudinary = async (public_id, resource_type = 'image') => {
        if (!public_id) return;
        const timestamp = Math.round((new Date()).getTime() / 1000);
        const params = { public_id: public_id, timestamp: timestamp };
        const signature = await generateSignature(params, API_SECRET);

        const formData = new FormData();
        formData.append("public_id", public_id); formData.append("api_key", API_KEY);
        formData.append("timestamp", timestamp); formData.append("signature", signature);
        
        const type = resource_type === 'raw' ? 'raw' : 'image';
        await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${type}/destroy`, { method: "POST", body: formData });
    };

    // --- Client Handlers ---
    const handleAddCompany = async (e) => {
        e.preventDefault();
        if (!newCompanyName.trim() || !newCustomerName.trim()) { setMessage('Error: Name and Customer Name required.'); return; }
        setIsUploading(true); setMessage('Processing...');
        try {
            const newCompanyRef = doc(collection(db, "companies"));
            const newCompanyId = newCompanyRef.id;
            let uploadedFiles = [];
            if (newAddFile) {
                setMessage('Uploading file...');
                const uploadResult = await uploadToCloudinary(newAddFile, newCompanyId);
                uploadedFiles.push(uploadResult);
            }
            setMessage('Saving profile...');
            await setDoc(newCompanyRef, {
                name: newCompanyName, customerName: newCustomerName, address: newAddress, phone: newPhone, 
                files: uploadedFiles, createdAt: serverTimestamp()
            });
            setMessage(`Success: "${newCompanyName}" added!`);
            setNewCompanyName(''); setNewCustomerName(''); setNewAddress(''); setNewPhone(''); setNewAddFile(null); 
            const fileInput = document.getElementById('add-file-input');
            if(fileInput) fileInput.value = '';
            fetchCompanies();
        } catch (error) { console.error(error); setMessage(`Error: ${error.message}`); }
        setIsUploading(false);
    };

    const openEditModal = (company) => {
        setEditingCompany(company); 
        setEditCompanyName(company.name || ''); setEditCustomerName(company.customerName || '');
        setEditAddress(company.address || ''); setEditPhone(company.phone || '');
        setNewEditFile(null); setModalOpen(true); setMessage('');
    };

    const handleDeleteFile = async (fileToDelete) => {
        if (!editingCompany || !fileToDelete) return;
        if (!window.confirm(`Delete file: ${fileToDelete.fileName}?`)) return;
        
        setIsUpdating(true); setMessage('Deleting file...');
        try {
            if (fileToDelete.public_id) await deleteFromCloudinary(fileToDelete.public_id, fileToDelete.resource_type || 'image');
            const companyRef = doc(db, "companies", editingCompany.id);
            const companySnap = await getDoc(companyRef);
            if (companySnap.exists()) {
                const currentFiles = companySnap.data().files || [];
                const updatedFiles = currentFiles.filter(f => f.id !== fileToDelete.id);
                await updateDoc(companyRef, { files: updatedFiles });
                setEditingCompany(prev => ({ ...prev, files: updatedFiles }));
                setCompanies(prevList => prevList.map(c => c.id === editingCompany.id ? { ...c, files: updatedFiles } : c));
                setMessage("File deleted.");
            }
        } catch (error) { setMessage(`Error deleting: ${error.message}`); }
        setIsUpdating(false);
    };

    const handleUpdateCompany = async (e) => {
        if (e) e.preventDefault(); 
        if (!editingCompany) return;
        setIsUpdating(true); setMessage('Saving...');
        try {
            const companyRef = doc(db, "companies", editingCompany.id); 
            const docSnap = await getDoc(companyRef);
            let currentFiles = docSnap.exists() ? (docSnap.data().files || []) : [];
            if (newEditFile) {
                const uploadResult = await uploadToCloudinary(newEditFile, editingCompany.id);
                currentFiles = [...currentFiles, uploadResult];
                setNewEditFile(null); 
            }
            await updateDoc(companyRef, { name: editCompanyName, customerName: editCustomerName, address: editAddress, phone: editPhone, files: currentFiles });
            const updatedCompanyData = { ...editingCompany, name: editCompanyName, customerName: editCustomerName, address: editAddress, phone: editPhone, files: currentFiles };
            setEditingCompany(updatedCompanyData);
            setCompanies(prev => prev.map(c => c.id === editingCompany.id ? updatedCompanyData : c));
            setMessage("Saved successfully!");
        } catch (error) { setMessage(`Error: ${error.message}`); }
        setIsUpdating(false);
    };

    // --- Booking Agent Handlers ---
    const handleAddAgent = async (e) => {
        e.preventDefault();
        if (!newAgentName.trim()) { setMessage('Error: Agent Name required.'); return; }
        setIsUploading(true); setMessage('Saving Agent...');
        try {
            await setDoc(doc(collection(db, "booking_companies")), {
                name: newAgentName,
                contactName: newAgentContact,
                phone: newAgentPhone,
                address: newAgentAddress,
                createdAt: serverTimestamp()
            });
            setMessage(`Success: Agent "${newAgentName}" added!`);
            setNewAgentName(''); setNewAgentContact(''); setNewAgentPhone(''); setNewAgentAddress('');
            fetchBookingAgents();
        } catch (error) { setMessage(`Error: ${error.message}`); }
        setIsUploading(false);
    };

    const handleDeleteAgent = async (id, name) => {
        if (!window.confirm(`Are you sure you want to permanently delete Agent: ${name}?`)) return;
        try {
            await deleteDoc(doc(db, "booking_companies", id));
            setMessage(`Agent "${name}" deleted.`);
            fetchBookingAgents();
        } catch (error) { setMessage(`Error deleting: ${error.message}`); }
    };

    const openAgentEditModal = (agent) => {
        setEditingAgent(agent);
        setEditAgentName(agent.name || '');
        setEditAgentContact(agent.contactName || '');
        setEditAgentPhone(agent.phone || '');
        setEditAgentAddress(agent.address || '');
        setAgentModalOpen(true); setMessage('');
    };

    const handleUpdateAgent = async (e) => {
        e.preventDefault();
        if (!editingAgent) return;
        setIsUpdating(true); setMessage('Updating Agent...');
        try {
            await updateDoc(doc(db, "booking_companies", editingAgent.id), {
                name: editAgentName,
                contactName: editAgentContact,
                phone: editAgentPhone,
                address: editAgentAddress
            });
            setMessage("Agent Updated successfully!");
            fetchBookingAgents();
            setAgentModalOpen(false);
        } catch (error) { setMessage(`Error: ${error.message}`); }
        setIsUpdating(false);
    };

    // --- Styles ---
    const fileGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '15px', marginTop: '15px' };
    const fileBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '10px', padding: '15px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', height: '180px' };
    const tabStyle = (isActive) => ({ padding: '12px 25px', background: isActive ? '#0972d4' : '#e0e0e0', color: isActive ? 'white' : '#555', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', flexGrow: 1, transition: '0.3s' });

    return (
        <div className='setting-container' style={{paddingBottom: '50px'}}>
            <p className='new-invoice-heading'>Settings & Documents</p>

            <div className='form-section' style={{borderLeft: '5px solid #e67e22', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                <div>
                    <h3 style={{color: '#333', fontSize: '18px', margin: 0}}>Service Rates Configuration</h3>
                    <p style={{color: '#666', fontSize: '14px', margin: '5px 0 0'}}>Manage DHL, FedEx, UPS rates and other charges.</p>
                </div>
                <button onClick={() => navigate('/dashboard/rates')} className='add-btn' style={{margin: 0, background: '#e67e22', border: 'none'}}>Manage Rates</button>
            </div>

            {/* --- Tabs Navigation --- */}
            <div style={{display: 'flex', gap: '5px', marginBottom: '20px', borderBottom: '3px solid #0972d4'}}>
                <button onClick={() => {setActiveTab('clients'); setMessage('');}} style={tabStyle(activeTab === 'clients')}>👥 Manage Clients / Profiles</button>
                <button onClick={() => {setActiveTab('agents'); setMessage('');}} style={tabStyle(activeTab === 'agents')}>✈️ Manage Booking Agents</button>
            </div>

            {/* =========================================
                TAB 1: CLIENTS / PROFILES 
            ========================================= */}
            {activeTab === 'clients' && (
                <>
                    <div className='form-section' style={{boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderRadius: '0 10px 10px 10px', marginTop: '-20px'}}>
                        <h3 style={{color: '#333', fontSize: '20px', marginBottom: '20px'}}>Add New Profile</h3>
                        <form onSubmit={handleAddCompany} className='form-grid'>
                            <div className='form-grid-item-full'><label>Company Name <span style={{color:'red'}}>*</span></label><input value={newCompanyName} onChange={e => setNewCompanyName(e.target.value)} placeholder='Company Name' disabled={isUploading} required style={{padding: '12px'}} /></div>
                            <div><label>Customer Name <span style={{color:'red'}}>*</span></label><input value={newCustomerName} onChange={e => setNewCustomerName(e.target.value)} placeholder='Owner Name' disabled={isUploading} required style={{padding: '12px'}} /></div>
                            <div><label>Phone</label><input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder='01xxxxxxxxx' disabled={isUploading} style={{padding: '12px'}} /></div>
                            <div className='form-grid-item-full'><label>Address</label><input value={newAddress} onChange={e => setNewAddress(e.target.value)} placeholder='Full Address' disabled={isUploading} style={{padding: '12px'}} /></div>
                            <div className='form-grid-item-full' style={{backgroundColor: '#f1f8ff', padding: '20px', borderRadius: '8px', marginTop: '15px', border: '1px dashed #0972d4'}}>
                                <label style={{marginBottom: '10px', display: 'block', fontWeight: 'bold', color: '#0972d4'}}>Upload Document / Logo (Optional)</label>
                                <input type="file" id="add-file-input" onChange={e => setNewAddFile(e.target.files[0])} disabled={isUploading} style={{backgroundColor: '#fff', border: '1px solid #ddd', padding: '10px', width: '100%', borderRadius: '5px'}} />
                            </div>
                            <button type='submit' className='add-btn' style={{marginTop: '20px', padding: '12px', fontSize: '16px'}} disabled={isUploading}>{isUploading ? 'Processing...' : '+ Add Profile'}</button>
                        </form>
                        {message && !modalOpen && <div style={{marginTop: '15px', padding: '10px', borderRadius: '5px', backgroundColor: message.includes('Error') ? '#ffebee' : '#e8f5e9', color: message.includes('Error') ? '#c62828' : '#2e7d32', textAlign: 'center'}}>{message}</div>}
                    </div>

                    <div className='form-section' style={{boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderRadius: '10px'}}>
                        <h3 style={{color: '#444', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>Registered Profiles <span style={{fontSize: '14px', fontWeight: 'normal', backgroundColor: '#e9ecef', padding: '5px 10px', borderRadius: '20px'}}>{companies.length} Found</span></h3>
                        {loading ? <p>Loading data...</p> : (
                            <div style={{overflowX: 'auto'}}>
                                <table className='invoice-preview-table' style={{borderCollapse: 'separate', borderSpacing: '0 8px', marginTop: '10px'}}>
                                    <thead><tr style={{background: '#f8f9fa', color: '#666'}}><th style={{padding: '15px'}}>Company</th><th style={{padding: '15px'}}>Customer</th><th style={{padding: '15px'}}>Files</th><th style={{padding: '15px', textAlign: 'center'}}>Actions</th></tr></thead>
                                    <tbody>{companies.map(company => (<tr key={company.id} style={{background: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}><td style={{padding: '15px', fontWeight: 'bold'}}>{company.name}</td><td style={{padding: '15px'}}>{company.customerName}</td><td style={{padding: '15px'}}>{company.files.length > 0 ? `${company.files.length} Docs` : 'No Files'}</td><td style={{padding: '15px', textAlign: 'center'}}><button className='view-btn' onClick={() => { setViewingCompany(company); setViewModalOpen(true); }}>View</button> <button className='edit-btn' onClick={() => openEditModal(company)}>Manage</button></td></tr>))}</tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* =========================================
                TAB 2: BOOKING AGENTS 
            ========================================= */}
            {activeTab === 'agents' && (
                <>
                    <div className='form-section' style={{boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderRadius: '0 10px 10px 10px', marginTop: '-20px'}}>
                        <h3 style={{color: '#333', fontSize: '20px', marginBottom: '20px'}}>Add New Booking Agent</h3>
                        <form onSubmit={handleAddAgent} className='form-grid'>
                            <div className='form-grid-item-full'><label>Agent Name / Company <span style={{color:'red'}}>*</span></label><input value={newAgentName} onChange={e => setNewAgentName(e.target.value)} placeholder='Agent Name' disabled={isUploading} required style={{padding: '12px'}} /></div>
                            <div><label>Contact Person</label><input value={newAgentContact} onChange={e => setNewAgentContact(e.target.value)} placeholder='Person Name' disabled={isUploading} style={{padding: '12px'}} /></div>
                            <div><label>Phone</label><input value={newAgentPhone} onChange={e => setNewAgentPhone(e.target.value)} placeholder='01xxxxxxxxx' disabled={isUploading} style={{padding: '12px'}} /></div>
                            <div className='form-grid-item-full'><label>Address</label><input value={newAgentAddress} onChange={e => setNewAgentAddress(e.target.value)} placeholder='Agent Address' disabled={isUploading} style={{padding: '12px'}} /></div>
                            
                            <button type='submit' className='add-btn' style={{marginTop: '20px', padding: '12px', fontSize: '16px'}} disabled={isUploading}>{isUploading ? 'Saving...' : '+ Add Agent'}</button>
                        </form>
                        {message && !agentModalOpen && <div style={{marginTop: '15px', padding: '10px', borderRadius: '5px', backgroundColor: message.includes('Error') ? '#ffebee' : '#e8f5e9', color: message.includes('Error') ? '#c62828' : '#2e7d32', textAlign: 'center'}}>{message}</div>}
                    </div>

                    <div className='form-section' style={{boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderRadius: '10px'}}>
                        <h3 style={{color: '#444', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>Booking Agents List <span style={{fontSize: '14px', fontWeight: 'normal', backgroundColor: '#e9ecef', padding: '5px 10px', borderRadius: '20px'}}>{bookingAgents.length} Found</span></h3>
                        <div style={{overflowX: 'auto'}}>
                            <table className='invoice-preview-table' style={{borderCollapse: 'separate', borderSpacing: '0 8px', marginTop: '10px'}}>
                                <thead><tr style={{background: '#f8f9fa', color: '#666'}}><th style={{padding: '15px'}}>Agent Name</th><th style={{padding: '15px'}}>Contact Person</th><th style={{padding: '15px'}}>Phone</th><th style={{padding: '15px', textAlign: 'center'}}>Actions</th></tr></thead>
                                <tbody>{bookingAgents.map(agent => (
                                    <tr key={agent.id} style={{background: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
                                        <td style={{padding: '15px', fontWeight: 'bold'}}>{agent.name}</td>
                                        <td style={{padding: '15px'}}>{agent.contactName || '-'}</td>
                                        <td style={{padding: '15px'}}>{agent.phone || '-'}</td>
                                        <td style={{padding: '15px', textAlign: 'center'}}>
                                            <button className='edit-btn' style={{marginRight: '5px'}} onClick={() => openAgentEditModal(agent)}>Edit</button> 
                                            <button className='delete-btn' style={{background: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer'}} onClick={() => handleDeleteAgent(agent.id, agent.name)}>Remove</button>
                                        </td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* --- CLIENT EDIT Modal --- */}
            {modalOpen && editingCompany && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{maxWidth: '800px', borderRadius: '15px', padding: '0', overflow: 'hidden'}}>
                        <div style={{background: '#0972d4', padding: '20px 25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h3 style={{color: 'white', margin: 0, fontSize: '20px', border: 'none'}}>Edit Profile & Files</h3>
                            <button onClick={() => {setModalOpen(false); setEditingCompany(null);}} style={{background: 'transparent', border: 'none', color: 'white', fontSize: '30px', cursor: 'pointer', lineHeight: '20px'}}>&times;</button>
                        </div>
                        <div style={{padding: '25px', maxHeight: '80vh', overflowY: 'auto', backgroundColor: '#f9f9f9'}}>
                            <form onSubmit={handleUpdateCompany}>
                                <div className='form-grid'>
                                    <div className='form-grid-item-full'> <label>Company Name</label> <input value={editCompanyName} onChange={e => setEditCompanyName(e.target.value)} style={{padding: '10px'}} /> </div>
                                    <div> <label>Customer</label> <input value={editCustomerName} onChange={e => setEditCustomerName(e.target.value)} style={{padding: '10px'}} /> </div>
                                    <div> <label>Phone</label> <input value={editPhone} onChange={e => setEditPhone(e.target.value)} style={{padding: '10px'}} /> </div>
                                </div>
                                <div style={{marginTop: '30px'}}>
                                    <h4 style={{fontSize: '18px', color: '#333', borderBottom: '2px solid #ddd', paddingBottom: '10px', marginBottom: '20px'}}>Documents & Files</h4>
                                    <div style={{background: '#fff', padding: '20px', borderRadius: '10px', border: '1px dashed #bbb', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '15px'}}>
                                        <div style={{flexGrow: 1}}><label style={{fontSize: '14px', marginBottom: '5px', display: 'block', color: '#666'}}>Choose New File:</label><input type='file' onChange={e => setNewEditFile(e.target.files[0])} style={{fontSize: '14px', width: '100%'}} /></div>
                                        <button type="button" onClick={() => handleUpdateCompany(null)} disabled={!newEditFile || isUpdating} className='add-btn' style={{padding: '10px 20px', fontSize: '14px', margin: 0, height: 'auto'}}>{isUpdating && newEditFile ? 'Uploading...' : 'Upload Now'}</button>
                                    </div>
                                    {editingCompany.files?.length === 0 ? <p style={{textAlign: 'center', color: '#999', padding: '20px'}}>No documents.</p> : (
                                        <div style={fileGridStyle}>
                                            {editingCompany.files?.map(file => (
                                                <div key={file.id} style={fileBoxStyle} title={file.fileName}>
                                                    <div style={{fontSize: '50px', marginTop: '10px'}}>{file.fileName.toLowerCase().endsWith('.pdf') ? '📄' : '🖼️'}</div>
                                                    <p style={{fontSize: '13px', textAlign: 'center', margin: '10px 0', wordBreak: 'break-all', lineHeight: '1.4', height: '40px', overflow: 'hidden'}}>{file.fileName.length > 20 ? file.fileName.substring(0, 18) + '...' : file.fileName}</p>
                                                    <div style={{display: 'flex', gap: '5px', width: '100%'}}>
                                                        <button type="button" onClick={() => setPreviewFile(file)} style={{flex: 1, padding: '5px', fontSize: '12px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>Preview</button>
                                                        <button type="button" onClick={() => handleDeleteFile(file)} style={{flex: 1, padding: '5px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>Delete</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div style={{marginTop: '30px', textAlign: 'right', borderTop: '1px solid #ddd', paddingTop: '20px'}}>
                                    <button type='submit' className='add-btn' disabled={isUpdating} style={{padding: '12px 25px', fontSize: '16px'}}>{isUpdating ? 'Saving Changes...' : 'Save All Changes'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* --- AGENT EDIT Modal --- */}
            {agentModalOpen && editingAgent && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{maxWidth: '600px', borderRadius: '15px', padding: '0', overflow: 'hidden'}}>
                        <div style={{background: '#0972d4', padding: '20px 25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h3 style={{color: 'white', margin: 0, fontSize: '20px', border: 'none'}}>Edit Booking Agent</h3>
                            <button onClick={() => {setAgentModalOpen(false); setEditingAgent(null);}} style={{background: 'transparent', border: 'none', color: 'white', fontSize: '30px', cursor: 'pointer', lineHeight: '20px'}}>&times;</button>
                        </div>
                        <div style={{padding: '25px', backgroundColor: '#f9f9f9'}}>
                            <form onSubmit={handleUpdateAgent}>
                                <div className='form-grid'>
                                    <div className='form-grid-item-full'> <label>Agent Name / Company</label> <input value={editAgentName} onChange={e => setEditAgentName(e.target.value)} required style={{padding: '10px'}} /> </div>
                                    <div> <label>Contact Person</label> <input value={editAgentContact} onChange={e => setEditAgentContact(e.target.value)} style={{padding: '10px'}} /> </div>
                                    <div> <label>Phone</label> <input value={editAgentPhone} onChange={e => setEditAgentPhone(e.target.value)} style={{padding: '10px'}} /> </div>
                                    <div className='form-grid-item-full'> <label>Address</label> <input value={editAgentAddress} onChange={e => setEditAgentAddress(e.target.value)} style={{padding: '10px'}} /> </div>
                                </div>
                                <div style={{marginTop: '20px', textAlign: 'right', borderTop: '1px solid #ddd', paddingTop: '15px'}}>
                                    <button type='submit' className='add-btn' disabled={isUpdating} style={{padding: '12px 25px', fontSize: '16px'}}>{isUpdating ? 'Updating...' : 'Update Agent'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CLIENT VIEW Modal --- */}
            {viewModalOpen && viewingCompany && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{maxWidth: '700px', borderRadius: '15px', padding: '0', overflow: 'hidden'}}>
                         <div style={{background: '#6c757d', padding: '20px 25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h3 style={{color: 'white', margin: 0, fontSize: '20px', border: 'none'}}>{viewingCompany.name}</h3>
                            <button onClick={() => setViewModalOpen(false)} style={{background: 'transparent', border: 'none', color: 'white', fontSize: '30px', cursor: 'pointer', lineHeight: '20px'}}>&times;</button>
                        </div>
                        <div style={{padding: '30px'}}>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px', background: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
                                <div><label style={{fontSize: '12px', color: '#888'}}>Customer</label><p style={{margin: '5px 0', fontWeight: 'bold', fontSize: '16px'}}>{viewingCompany.customerName}</p></div>
                                <div><label style={{fontSize: '12px', color: '#888'}}>Phone</label><p style={{margin: '5px 0', fontWeight: 'bold', fontSize: '16px'}}>{viewingCompany.phone || 'N/A'}</p></div>
                                <div style={{gridColumn: 'span 2'}}><label style={{fontSize: '12px', color: '#888'}}>Address</label><p style={{margin: '5px 0', fontWeight: 'bold', fontSize: '16px'}}>{viewingCompany.address || 'N/A'}</p></div>
                            </div>
                            <h4 style={{borderBottom: '2px solid #eee', paddingBottom: '10px', color: '#0972d4', marginBottom: '20px'}}>Documents</h4>
                            {viewingCompany.files?.length === 0 ? <p style={{color: '#ccc', fontStyle: 'italic', textAlign: 'center'}}>No documents.</p> : (
                                <div style={fileGridStyle}>
                                    {viewingCompany.files?.map(file => (
                                        <div key={file.id} onClick={() => setPreviewFile(file)} style={{...fileBoxStyle, height: '160px', cursor: 'pointer', borderColor: '#eee'}}>
                                            <div style={{fontSize: '40px', marginTop: '15px'}}>{file.fileName.toLowerCase().endsWith('.pdf') ? '📄' : '🖼️'}</div>
                                            <p style={{fontSize: '12px', textAlign: 'center', margin: '10px 0', height: '35px', overflow: 'hidden'}}>{file.fileName}</p>
                                            <span style={{fontSize: '11px', color: '#0972d4', fontWeight: 'bold', border: '1px solid #0972d4', padding: '2px 8px', borderRadius: '10px'}}>Click to View</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* --- PREVIEW OVERLAY --- */}
            {previewFile && (
                <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <div style={{position: 'relative', width: '90%', height: '90%', background: 'white', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                        <div style={{padding: '15px 20px', background: '#222', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <span style={{fontSize: '16px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{previewFile.fileName}</span>
                            <div style={{display: 'flex', gap: '10px'}}>
                                <a href={getDownloadUrl(previewFile.fileUrl)} className='add-btn' style={{textDecoration:'none', display: 'flex', alignItems: 'center', height: 'auto', lineHeight: 'normal', padding: '8px 15px', color: 'white', margin: 0, fontSize: '13px'}}>Download</a>
                                <button onClick={() => setPreviewFile(null)} style={{background: '#dc3545', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px'}}>CLOSE X</button>
                            </div>
                        </div>
                        <div style={{flex: 1, overflow: 'auto', background: '#e9ecef', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px'}}>
                            {previewFile.fileName.toLowerCase().endsWith('.pdf') ? (
                                <iframe src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewFile.fileUrl)}&embedded=true`} title="Preview" style={{width: '100%', height: '100%', border: 'none', borderRadius: '5px', boxShadow: '0 5px 15px rgba(0,0,0,0.2)'}}></iframe>
                            ) : (
                                <img src={previewFile.fileUrl} alt="Preview" style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '5px', boxShadow: '0 5px 15px rgba(0,0,0,0.2)'}} />
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default Setting;